// apps/admin/src/pages/MobileAppPortal.js
// 100% Mobile-Native Application for Ownezzy
// Merges 100+ Ready-to-Use Store Websites + Dedicated Mobile Store Admin
// Features: 
//   - Bottom Navigation Tabs (Stores, Search, My Store, Account)
//   - Zero-Lag Virtualized Feed with Lightweight WebP Previews
//   - Dedicated Mobile Screens: Orders, Products, Settings, Customers, Share & QR
//   - Mobile Bottom Sheets with Sticky Headers & Sticky Footers (No Zoom, No Keyboard Collision)
//   - Direct UPI Setup (0% Commission) & WhatsApp Order Alerts
//   - Transparent Pricing: ₹399/month (7-Day Free Trial, ₹0 Today)

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, setUser, logout } from "../redux/Slice/authSlice";
import { CATEGORY_FILTERS, DEMO_STORES } from "../data/demoStoresData";
import { login as apiLogin, sendOnboardingOtp, verifyOnboardingOtp, chooseAccount } from "../services/auth";
import { client } from "../services";
import { getSites, getSiteSettings, updateSiteSettings } from "../services/sites";
import { getOrders, updateOrder } from "../services/orders";
import { getProducts, createProduct, updateProduct, deleteProduct } from "../services/products";
import { uploadMedia } from "../services/media";
import { getPendingOrders } from "../services/admin";
import { money, dateText } from "../utils/format";
import AdminPanel from "./AdminPanel";
import "./MobileAppPortal.css";

const PAGE_SIZE = 12;

export default function MobileAppPortal({ notify: parentNotify, defaultTab = "stores" }) {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  // Active Bottom Tab: 'stores' | 'search' | 'admin' | 'account'
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Search, Category & Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("popular"); // 'popular' | 'name-asc' | 'name-desc' | 'v2' | 'intl'

  // Feed Pagination
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Active Modals & Bottom Sheets
  const [previewStore, setPreviewStore] = useState(null);
  const [launchingStore, setLaunchingStore] = useState(null);
  const [authSheetOpen, setAuthSheetOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  // Global pending orders count for navigation badge
  const [globalPendingOrders, setGlobalPendingOrders] = useState(0);

  // Toast Notification
  const [toast, setToast] = useState(null);
  const notify = useCallback(
    (msg, type = "info") => {
      setToast({ msg, type });
      if (parentNotify) parentNotify(msg, type);
      setTimeout(() => setToast(null), 3500);
    },
    [parentNotify]
  );

  // Debounce search query to eliminate all typing stutter
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedQuery(searchQuery), 120);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Reset pagination when category, search, or sort changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [debouncedQuery, selectedCategory, sortBy]);

  // Periodic pending orders check for badge
  useEffect(() => {
    if (!user) return undefined;
    let live = true;
    const fetchBadge = () => {
      getPendingOrders()
        .then((res) => live && setGlobalPendingOrders(res.data?.pending || 0))
        .catch(() => {});
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 45000);
    return () => {
      live = false;
      clearInterval(interval);
    };
  }, [user]);

  // Filtered & Sorted Stores Catalog (100+ stores)
  const filteredStores = useMemo(() => {
    const q = debouncedQuery.toLowerCase().trim();
    const catConfig = CATEGORY_FILTERS.find((c) => c.id === selectedCategory);

    let list = DEMO_STORES.filter((store) => {
      // Category filter
      let matchesCat = true;
      if (selectedCategory !== "all") {
        if (selectedCategory === "intl") {
          matchesCat = Boolean(store.isIntl);
        } else if (catConfig && catConfig.matches) {
          matchesCat = catConfig.matches.includes(store.baseId || store.template || store.id);
        }
      }
      if (!matchesCat) return false;

      // Search match
      if (!q) return true;
      return (
        store.name.toLowerCase().includes(q) ||
        store.label.toLowerCase().includes(q) ||
        (store.note && store.note.toLowerCase().includes(q)) ||
        (store.pitch && store.pitch.toLowerCase().includes(q)) ||
        (store.business && store.business.toLowerCase().includes(q)) ||
        (store.country && store.country.toLowerCase().includes(q))
      );
    });

    // Sorting
    if (sortBy === "name-asc") {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "name-desc") {
      list = [...list].sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === "v2") {
      list = [...list].sort((a, b) => (b.isV2 ? 1 : 0) - (a.isV2 ? 1 : 0));
    } else if (sortBy === "intl") {
      list = [...list].sort((a, b) => (b.isIntl ? 1 : 0) - (a.isIntl ? 1 : 0));
    }

    return list;
  }, [debouncedQuery, selectedCategory, sortBy]);

  // Paginated slice for 60 FPS scrolling
  const displayedStores = useMemo(() => {
    return filteredStores.slice(0, visibleCount);
  }, [filteredStores, visibleCount]);

  const hasMore = visibleCount < filteredStores.length;
  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredStores.length));
  };

  return (
    <div className="native-app-root">
      {/* Toast Notification */}
      {toast && (
        <div className={`native-toast ${toast.type}`}>
          <span className="toast-icon">{toast.type === "error" ? "⚠️" : "✓"}</span>
          <span className="toast-text">{toast.msg}</span>
        </div>
      )}

      {/* Native App Top Header */}
      <header className="native-top-appbar">
        <div className="native-appbar-brand" onClick={() => setActiveTab("stores")}>
          <img
            src="/brand/icon-192.png"
            alt="Ownezzy"
            className="native-brand-icon"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <span className="native-brand-logo">
            <span className="brand-owne">owne</span>
            <span className="brand-zzy">zzy</span>
          </span>
          <span className="native-brand-pill">
            {user?.role === "superadmin" ? "SUPER" : "APP"}
          </span>
        </div>

        <div className="native-appbar-actions">
          {user ? (
            <button
              type="button"
              className="native-user-chip"
              onClick={() => setActiveTab("admin")}
              title={`My Store: ${user.name}`}
            >
              <span className="chip-avatar">{user.role === "superadmin" ? "👑" : user.name.charAt(0).toUpperCase()}</span>
              <span className="chip-name">{user.role === "superadmin" ? "Super Admin" : user.name.split(" ")[0]}</span>
            </button>
          ) : (
            <button
              type="button"
              className="native-header-signin-btn"
              onClick={() => {
                setAuthMode("login");
                setAuthSheetOpen(true);
              }}
            >
              Sign In
            </button>
          )}
        </div>
      </header>

      {/* Top Switcher: Store View & Admin View */}
      <div className="native-top-mode-switcher-bar">
        <div className="native-mode-switcher-container">
          <button
            type="button"
            className={`mode-toggle-btn ${activeTab === "stores" || activeTab === "search" ? "active" : ""}`}
            onClick={() => setActiveTab("stores")}
          >
            <span className="mode-toggle-icon">🏪</span>
            <span className="mode-toggle-label">Store View</span>
          </button>
          <button
            type="button"
            className={`mode-toggle-btn ${activeTab === "admin" || activeTab === "account" ? "active" : ""}`}
            onClick={() => setActiveTab("admin")}
          >
            <span className="mode-toggle-icon">💼</span>
            <span className="mode-toggle-label">Admin View</span>
            {globalPendingOrders > 0 && (
              <span className="mode-pending-badge">{globalPendingOrders}</span>
            )}
          </button>
        </div>
      </div>

      {/* App Body - Main View Switching */}
      <main className="native-app-body">
        {/* TAB 1: STORES DISCOVERY FEED */}
        {activeTab === "stores" && (
          <div className="native-tab-view stores-feed-view">
            {/* Native Mobile Hero Banner */}
            <div className="native-hero-card">
              <div className="hero-eyebrow">
                <span className="eyebrow-dot" />
                <span>100+ STORE WEBSITES • ₹399/MONTH</span>
              </div>
              <h1 className="hero-heading">Launch Your Online Store in 60s</h1>
              <p className="hero-subtext">
                Pick a ready-made website for your business. 7 days free, then just ₹399/mo. 0% commission, direct UPI &amp; WhatsApp alerts.
              </p>

              <div className="hero-perks-row">
                <span className="hero-perk">⚡ Instant 60s Live</span>
                <span className="hero-perk">💰 0% Commission</span>
                <span className="hero-perk">🎁 7 Days Free</span>
              </div>
            </div>

            {/* Quick Search Field in Explore */}
            <div className="native-search-container">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="native-search-input"
                placeholder="Search 100+ stores: Kurtis, Plants, Bakery, Gifts…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Horizontal Categories Scroll */}
            <div className="native-category-scroller">
              {CATEGORY_FILTERS.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`native-cat-chip ${selectedCategory === cat.id ? "active" : ""}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <span className="cat-chip-icon">{cat.icon}</span>
                  <span className="cat-chip-label">{cat.label}</span>
                </button>
              ))}
            </div>

            {/* Feed Results Toolbar */}
            <div className="native-feed-toolbar">
              <span className="feed-count">
                Showing <b>{filteredStores.length}</b> stores
                {selectedCategory !== "all" && ` in ${CATEGORY_FILTERS.find((c) => c.id === selectedCategory)?.label}`}
              </span>

              <select
                className="native-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort templates"
              >
                <option value="popular">Recommended</option>
                <option value="name-asc">A → Z</option>
                <option value="name-desc">Z → A</option>
                <option value="v2">Studio v2</option>
                <option value="intl">International</option>
              </select>
            </div>

            {/* Stores List (Mobile-Optimized Native Cards) */}
            <div className="native-stores-list">
              {displayedStores.map((store) => (
                <article key={store.id} className="native-store-card">
                  {/* Browser Snapshot Frame */}
                  <div
                    className="card-preview-frame"
                    onClick={() => setPreviewStore(store)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setPreviewStore(store)}
                  >
                    <div className="card-browser-mockup">
                      <span className="browser-dots">
                        <i />
                        <i />
                        <i />
                      </span>
                      <span className="browser-slug">{store.demo}.ownezzy.com</span>
                    </div>

                    <div className="card-image-holder">
                      <img
                        src={store.preview}
                        alt={`${store.name} - ${store.label}`}
                        className="card-preview-image"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <div
                        className="card-image-fallback"
                        style={{
                          background: `linear-gradient(135deg, ${store.theme?.main || "#10b981"}, #0f172a)`,
                        }}
                      >
                        <span style={{ fontSize: "36px" }}>{store.icon}</span>
                        <span>{store.name}</span>
                      </div>
                    </div>

                    {/* Overlay Badges */}
                    <div className="card-overlay-badges">
                      <span className="badge-live">
                        <span className="live-pulse-dot" /> Live Demo
                      </span>
                      {store.isV2 && <span className="badge-v2">Studio v2</span>}
                      {store.isIntl && <span className="badge-intl">🌍 {store.country}</span>}
                    </div>

                    <span className="badge-category">
                      <span>{store.icon}</span>
                      <span>{store.label}</span>
                    </span>
                  </div>

                  {/* Card Content Details */}
                  <div className="card-content-body">
                    <div className="card-title-row">
                      <h2 className="card-store-name">{store.name}</h2>
                      <span className="card-store-niche">{store.label}</span>
                    </div>

                    <p className="card-store-pitch">{store.pitch || store.note}</p>

                    {/* Pricing Row */}
                    <div className="card-pricing-row">
                      <div className="pricing-left">
                        <span className="price-tag">₹399</span>
                        <span className="price-unit">/ month</span>
                      </div>
                      <div className="pricing-badges">
                        <span className="trial-pill">7-Day Free Trial</span>
                        <span className="zero-comm-pill">0% Commission</span>
                      </div>
                    </div>

                    {/* Touch Action Buttons */}
                    <div className="card-action-buttons">
                      <button
                        type="button"
                        className="btn-card-preview"
                        onClick={() => setPreviewStore(store)}
                      >
                        <span>👁️ Live Demo</span>
                      </button>
                      <button
                        type="button"
                        className="btn-card-launch"
                        onClick={() => setLaunchingStore(store)}
                      >
                        <span>⚡ Get This Store</span>
                      </button>
                    </div>

                    <div className="card-foot-notice">
                      7 days free • ₹399/mo after • Cancel anytime
                    </div>
                  </div>
                </article>
              ))}

              {filteredStores.length === 0 && (
                <div className="native-empty-state">
                  <span className="empty-icon">🔍</span>
                  <h3>No matching websites found</h3>
                  <p>Try searching for clothing, dry fruits, bakery, jewellery, or gifts.</p>
                  <button
                    type="button"
                    className="btn-reset-filters"
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory("all");
                    }}
                  >
                    View All 100+ Websites
                  </button>
                </div>
              )}
            </div>

            {/* Load More Pagination Button */}
            {hasMore && (
              <div className="native-load-more-wrap">
                <button type="button" className="btn-native-load-more" onClick={loadMore}>
                  <span>Load More Stores</span>
                  <span className="load-more-counter">
                    ({displayedStores.length} of {filteredStores.length})
                  </span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* TAB 2: SEARCH & DISCOVERY BY NICHE */}
        {activeTab === "search" && (
          <div className="native-tab-view search-view">
            <div className="native-section-header">
              <h2>Browse by Business Category</h2>
              <p>Explore 100+ specialized ready-to-use e-commerce store templates</p>
            </div>

            <div className="native-search-container" style={{ margin: "0 0 16px 0" }}>
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="native-search-input"
                placeholder="Search all businesses &amp; products…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="native-category-grid">
              {CATEGORY_FILTERS.map((cat) => {
                const count =
                  cat.id === "all"
                    ? DEMO_STORES.length
                    : cat.id === "intl"
                    ? DEMO_STORES.filter((s) => s.isIntl).length
                    : DEMO_STORES.filter((s) => (cat.matches || []).includes(s.baseId || s.template || s.id)).length;

                return (
                  <button
                    key={cat.id}
                    type="button"
                    className={`native-cat-card ${selectedCategory === cat.id ? "active" : ""}`}
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      setActiveTab("stores");
                    }}
                  >
                    <span className="cat-card-icon">{cat.icon}</span>
                    <div className="cat-card-info">
                      <span className="cat-card-name">{cat.label}</span>
                      <span className="cat-card-count">{count} stores</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* TAB 3: MY STORE (NATIVE STORE ADMIN) */}
        {activeTab === "admin" && (
          <div className="native-tab-view admin-view">
            {user ? (
              <NativeStoreManager
                user={user}
                notify={notify}
                onBrowseStores={() => setActiveTab("stores")}
              />
            ) : (
              <div className="native-signin-prompt-card">
                <span className="signin-lock-icon">🔐</span>
                <h2>Sign In to Manage Your Store</h2>
                <p>
                  Access your orders, product catalog, customer delivery addresses, UPI payment setup, and store settings.
                </p>
                <button
                  type="button"
                  className="btn-native-primary"
                  onClick={() => {
                    setAuthMode("login");
                    setAuthSheetOpen(true);
                  }}
                >
                  Sign In to Admin Panel
                </button>
                <div style={{ marginTop: "14px" }}>
                  <button
                    type="button"
                    className="btn-native-secondary"
                    onClick={() => setActiveTab("stores")}
                  >
                    Don&apos;t have a store? Browse 100+ Stores
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 4: ACCOUNT, APK & SUPPORT */}
        {activeTab === "account" && (
          <div className="native-tab-view account-view">
            <div className="native-account-profile-card">
              {user ? (
                <>
                  <div className="account-avatar-large">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                  <h2 className="account-user-name">{user.name}</h2>
                  <span className="account-user-email">{user.email}</span>
                  <span className="account-user-role">Role: {user.role}</span>
                </>
              ) : (
                <>
                  <div className="account-avatar-large">👤</div>
                  <h2 className="account-user-name">Welcome to Ownezzy</h2>
                  <p className="account-user-email">Launch or manage your online store</p>
                  <button
                    type="button"
                    className="btn-native-primary"
                    style={{ margin: "12px auto 0", maxWidth: "200px" }}
                    onClick={() => {
                      setAuthMode("login");
                      setAuthSheetOpen(true);
                    }}
                  >
                    Sign In
                  </button>
                </>
              )}
            </div>

            {/* Transparent Pricing Card */}
            <div className="native-pricing-info-card">
              <div className="pic-header">
                <h3>💎 Transparent Ownezzy Pricing</h3>
                <span className="pic-badge">Official Plan</span>
              </div>
              <div className="pic-price-row">
                <span className="pic-price">₹399</span>
                <span className="pic-period">/ month per store</span>
              </div>
              <p className="pic-trial-note">Includes 7-day 100% Free Trial. Cancel anytime.</p>

              <div className="pic-perks-list">
                <div className="pic-perk">✓ 100+ business templates ready to launch in 60s</div>
                <div className="pic-perk">✓ Free subdomain (yourshop.ownezzy.com) &amp; SSL</div>
                <div className="pic-perk">✓ 0% commission on direct UPI (GPay/PhonePe/Paytm)</div>
                <div className="pic-perk">✓ Automated WhatsApp customer order alerts</div>
                <div className="pic-perk">✓ Full mobile admin panel &amp; Android APK access</div>
              </div>
            </div>

            {/* Mobile App Download & Share */}
            <div className="native-app-distribution-card">
              <h3>📱 Ownezzy Mobile App</h3>
              <p>Keep the official APK on your phone or share with fellow business owners.</p>
              <div className="app-dist-buttons">
                <a
                  href="/downloads/ownezzy.apk?v=2.0"
                  download="ownezzy.apk"
                  className="btn-dist-download"
                >
                  <span>⬇ Download Android APK (v2.0)</span>
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    "Launch your online store in 60 seconds with Ownezzy: 100+ ready store designs, ₹399/mo with 7-day free trial, 0% commission! Download app: https://admin.ownezzy.com/downloads/ownezzy.apk?v=2.0"
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-dist-share"
                >
                  <span>💬 Share on WhatsApp</span>
                </a>
              </div>
            </div>

            {/* Help & Logout */}
            <div className="native-account-actions-card">
              <a
                href="https://wa.me/918893725078?text=Hi%20Ownezzy%20Support,%20I%20need%20help%20with%20my%20mobile%20app"
                target="_blank"
                rel="noopener noreferrer"
                className="native-action-row"
              >
                <span>💬 WhatsApp Support</span>
                <span>›</span>
              </a>
              <a href="https://ownezzy.com/guide" target="_blank" rel="noopener noreferrer" className="native-action-row">
                <span>📖 User Guide &amp; Tutorials</span>
                <span>›</span>
              </a>
              {user && (
                <button
                  type="button"
                  className="native-action-row logout-row"
                  onClick={() => {
                    dispatch(logout());
                    notify("Signed out successfully", "info");
                    setActiveTab("stores");
                  }}
                >
                  <span>🚪 Sign Out</span>
                  <span>›</span>
                </button>
              )}
            </div>
          </div>
        )}
      </main>

      {/* NATIVE BOTTOM NAVIGATION BAR */}
      <nav className="native-bottom-nav" aria-label="Main Navigation">
        <button
          type="button"
          className={`nav-tab-item ${activeTab === "stores" ? "active" : ""}`}
          onClick={() => setActiveTab("stores")}
        >
          <span className="nav-tab-icon">🏪</span>
          <span className="nav-tab-label">Stores</span>
          <span className="nav-tab-badge">100+</span>
        </button>

        <button
          type="button"
          className={`nav-tab-item ${activeTab === "search" ? "active" : ""}`}
          onClick={() => setActiveTab("search")}
        >
          <span className="nav-tab-icon">🔍</span>
          <span className="nav-tab-label">Search</span>
        </button>

        <button
          type="button"
          className={`nav-tab-item ${activeTab === "admin" ? "active" : ""}`}
          onClick={() => {
            if (user) {
              setActiveTab("admin");
            } else {
              setAuthMode("login");
              setAuthSheetOpen(true);
            }
          }}
        >
          <span className="nav-tab-icon">📊</span>
          <span className="nav-tab-label">My Store</span>
          {globalPendingOrders > 0 && (
            <span className="nav-tab-badge alert-badge">{globalPendingOrders}</span>
          )}
        </button>

        <button
          type="button"
          className={`nav-tab-item ${activeTab === "account" ? "active" : ""}`}
          onClick={() => setActiveTab("account")}
        >
          <span className="nav-tab-icon">👤</span>
          <span className="nav-tab-label">Account</span>
        </button>
      </nav>

      {/* MODAL 1: NATIVE STORE PREVIEW & SHOWCASE SHEET */}
      {previewStore && (
        <MobileStorePreviewSheet
          store={previewStore}
          onClose={() => setPreviewStore(null)}
          onLaunchStore={(s) => {
            setPreviewStore(null);
            setLaunchingStore(s);
          }}
        />
      )}

      {/* MODAL 2: 60s STORE LAUNCHER & BUY SHEET */}
      {launchingStore && (
        <NativeStoreLauncherSheet
          store={launchingStore}
          user={user}
          notify={notify}
          onClose={() => setLaunchingStore(null)}
          onStoreCreated={() => {}}
          onGoToAdmin={() => {
            setLaunchingStore(null);
            setActiveTab("admin");
          }}
        />
      )}

      {/* MODAL 3: NATIVE AUTH SHEET (LOGIN & REGISTER) */}
      {authSheetOpen && (
        <NativeAuthSheet
          mode={authMode}
          onClose={() => setAuthSheetOpen(false)}
          notify={notify}
          onSuccess={(loggedInUser) => {
            setAuthSheetOpen(false);
            notify(`Welcome back, ${loggedInUser.name}!`, "success");
            setActiveTab("admin");
          }}
        />
      )}
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: NATIVE STORE MANAGER (MY STORE CONTROLLER & DASHBOARD)
// =========================================================================
function NativeStoreManager({ user, notify, onBrowseStores }) {
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [loadingSites, setLoadingSites] = useState(true);

  // Store Orders
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);

  // Store Products
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Active Subview: null (Dashboard) | 'orders' | 'products' | 'settings' | 'customers' | 'share' | 'fullAdmin'
  const [subView, setSubView] = useState(null);

  // Load User's Sites
  const loadSites = useCallback(async () => {
    try {
      setLoadingSites(true);
      const res = await getSites({ limit: 100, sort: "newest", demo: "include" });
      const fetched = res.data?.sites || [];
      setSites(fetched);
      if (fetched.length > 0) {
        setSelectedSiteId((prev) => (fetched.some((s) => s.id === prev) ? prev : fetched[0].id));
      }
    } catch (err) {
      notify(err.response?.data?.message || "Failed to load stores", "error");
    } finally {
      setLoadingSites(false);
    }
  }, [notify]);

  useEffect(() => {
    loadSites();
    getPendingOrders()
      .then((res) => setPendingOrdersCount(res.data?.pending || 0))
      .catch(() => {});
  }, [loadSites]);

  const selectedSite = useMemo(() => {
    return sites.find((s) => s.id === selectedSiteId) || sites[0] || null;
  }, [sites, selectedSiteId]);

  // Load Orders for Selected Site
  const loadOrders = useCallback(async () => {
    if (!selectedSite?.id) return;
    try {
      setLoadingOrders(true);
      const res = await getOrders(selectedSite.id, { limit: 100 });
      setOrders(res.data?.orders || []);
    } catch {
      // ignore
    } finally {
      setLoadingOrders(false);
    }
  }, [selectedSite]);

  // Load Products for Selected Site
  const loadProducts = useCallback(async () => {
    if (!selectedSite?.id) return;
    try {
      setLoadingProducts(true);
      const res = await getProducts(selectedSite.id, { limit: 100 });
      setProducts(res.data?.products || []);
    } catch {
      // ignore
    } finally {
      setLoadingProducts(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    if (selectedSite?.id) {
      loadOrders();
      loadProducts();
    }
  }, [selectedSite, loadOrders, loadProducts]);

  // Handle Order Status Update
  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    if (!selectedSite?.id) return;
    try {
      await updateOrder(selectedSite.id, orderId, { status: newStatus });
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      );
      notify(`Order #${orderId} marked as ${newStatus}`, "success");
    } catch (err) {
      notify(err.response?.data?.message || "Failed to update order status", "error");
    }
  };

  // Handle Mark Paid for UPI
  const handleMarkPaid = async (orderId) => {
    if (!selectedSite?.id) return;
    try {
      await updateOrder(selectedSite.id, orderId, { paymentStatus: "paid" });
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, paymentStatus: "paid" } : o))
      );
      notify(`Order #${orderId} marked as Paid`, "success");
    } catch (err) {
      notify(err.response?.data?.message || "Failed to mark paid", "error");
    }
  };

  // KPI Calculations
  const totalRevenue = useMemo(() => {
    return orders
      .filter((o) => o.status !== "Cancelled" && o.status !== "Refunded")
      .reduce((sum, o) => sum + (Number(o.total) || 0), 0);
  }, [orders]);

  const activePendingOrders = useMemo(() => {
    return orders.filter((o) => (o.status || "").toLowerCase() === "pending");
  }, [orders]);

  const completedOrdersCount = useMemo(() => {
    return orders.filter((o) => (o.status || "").toLowerCase() === "delivered").length;
  }, [orders]);

  // SUBVIEW 1: FULL WORKSPACE (FOR SUPERADMIN / ADVANCED TOOLS)
  if (subView === "fullAdmin") {
    return (
      <div className="native-subview-wrapper">
        <div className="native-subview-appbar">
          <button
            type="button"
            className="btn-subview-back"
            onClick={() => setSubView(null)}
          >
            ‹ Back to Dashboard
          </button>
          <span className="subview-header-title">Full Workspace</span>
        </div>
        <AdminPanel embedded={true} onBack={() => setSubView(null)} />
      </div>
    );
  }

  // SUBVIEW 2: DEDICATED MOBILE ORDERS VIEW
  if (subView === "orders") {
    return (
      <MobileOrdersView
        orders={orders}
        loading={loadingOrders}
        onRefresh={loadOrders}
        onBack={() => setSubView(null)}
        onUpdateStatus={handleUpdateOrderStatus}
        onMarkPaid={handleMarkPaid}
        storeName={selectedSite?.name}
      />
    );
  }

  // SUBVIEW 3: DEDICATED MOBILE PRODUCTS VIEW
  if (subView === "products") {
    return (
      <MobileProductsView
        siteId={selectedSite?.id}
        products={products}
        loading={loadingProducts}
        onRefresh={loadProducts}
        onBack={() => setSubView(null)}
        notify={notify}
      />
    );
  }

  // SUBVIEW 4: DEDICATED MOBILE STORE SETTINGS VIEW
  if (subView === "settings") {
    return (
      <MobileStoreSettingsView
        site={selectedSite}
        onBack={() => setSubView(null)}
        notify={notify}
        onSettingsUpdated={() => {
          loadSites();
          setSubView(null);
        }}
      />
    );
  }

  // SUBVIEW 5: DEDICATED MOBILE CUSTOMERS VIEW
  if (subView === "customers") {
    return (
      <MobileCustomersView
        orders={orders}
        onBack={() => setSubView(null)}
        storeName={selectedSite?.name}
      />
    );
  }

  // SUBVIEW 6: DEDICATED MOBILE SHARE & QR SCREEN
  if (subView === "share") {
    return (
      <MobileShareQrView
        site={selectedSite}
        onBack={() => setSubView(null)}
        notify={notify}
      />
    );
  }

  // DASHBOARD HOME VIEW
  return (
    <div className="native-store-dashboard">
      {/* Super Admin Notice */}
      {user.role === "superadmin" && (
        <div className="superadmin-mobile-banner">
          <span>👑 Super Admin: {sites.length} Active Stores</span>
          <button
            type="button"
            className="btn-sa-full"
            onClick={() => setSubView("fullAdmin")}
          >
            Open Full Workspace ›
          </button>
        </div>
      )}

      {/* Multi-Store Switcher (Horizontal Pills) */}
      {sites.length > 1 && (
        <div className="native-store-switcher-bar">
          <span className="switcher-label">Store:</span>
          <div className="switcher-scroller">
            {sites.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`switcher-pill ${selectedSite?.id === s.id ? "active" : ""}`}
                onClick={() => setSelectedSiteId(s.id)}
              >
                {s.name || s.slug}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty State: No Store Launched Yet */}
      {sites.length === 0 && !loadingSites ? (
        <div className="native-dashboard-empty">
          <span style={{ fontSize: "44px" }}>🚀</span>
          <h2>Launch Your First Store</h2>
          <p>
            Choose from 100+ business website templates with pre-loaded demo products, 0% commission direct UPI, and instant WhatsApp alerts.
          </p>
          <button type="button" className="btn-native-primary" onClick={onBrowseStores}>
            Browse 100+ Stores &amp; Launch (7 Days Free)
          </button>
        </div>
      ) : selectedSite ? (
        <>
          {/* Active Store Hero Card */}
          <div className="native-active-store-card">
            <div className="store-card-top-row">
              <div className="store-identity">
                <span className="store-avatar-circle">
                  {(selectedSite.name || "S").charAt(0).toUpperCase()}
                </span>
                <div>
                  <h2 className="store-card-name">{selectedSite.name}</h2>
                  <a
                    href={`https://${selectedSite.slug}.ownezzy.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="store-card-link"
                  >
                    {selectedSite.slug}.ownezzy.com ↗
                  </a>
                </div>
              </div>
              <span className="store-plan-badge">
                {selectedSite.plan === "trial" ? "7-Day Free Trial" : "₹399/mo Active"}
              </span>
            </div>

            <div className="store-quick-links-row">
              <a
                href={`https://${selectedSite.slug}.ownezzy.com`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-store-action-link"
              >
                🌐 View Store
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  `Check out our official online store: https://${selectedSite.slug}.ownezzy.com`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-store-action-link"
              >
                💬 Share on WA
              </a>
              <button
                type="button"
                className="btn-store-action-link"
                onClick={() => setSubView("settings")}
              >
                ⚙️ Settings
              </button>
            </div>
          </div>

          {/* 2x2 Real-Time KPI Grid */}
          <div className="native-kpi-grid">
            <div
              className={`kpi-card ${activePendingOrders.length > 0 ? "highlight-kpi" : ""}`}
              onClick={() => setSubView("orders")}
              role="button"
              tabIndex={0}
            >
              <div className="kpi-top">
                <span className="kpi-label">Pending Action</span>
                <span className="kpi-icon">⏳</span>
              </div>
              <span className="kpi-value">{activePendingOrders.length}</span>
              <span className="kpi-sub">Tap to process</span>
            </div>

            <div
              className="kpi-card"
              onClick={() => setSubView("orders")}
              role="button"
              tabIndex={0}
            >
              <div className="kpi-top">
                <span className="kpi-label">Total Orders</span>
                <span className="kpi-icon">📦</span>
              </div>
              <span className="kpi-value">{orders.length}</span>
              <span className="kpi-sub">All-time count</span>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <span className="kpi-label">Total Sales</span>
                <span className="kpi-icon">💰</span>
              </div>
              <span className="kpi-value">{money(totalRevenue)}</span>
              <span className="kpi-sub">0% platform fee</span>
            </div>

            <div
              className="kpi-card"
              onClick={() => setSubView("orders")}
              role="button"
              tabIndex={0}
            >
              <div className="kpi-top">
                <span className="kpi-label">Delivered</span>
                <span className="kpi-icon">🎉</span>
              </div>
              <span className="kpi-value">{completedOrdersCount}</span>
              <span className="kpi-sub">Fulfilled orders</span>
            </div>
          </div>

          {/* Quick Action Hub (6 Native Tiles) */}
          <div className="native-quick-actions-section">
            <h3 className="section-title">Quick Actions</h3>
            <div className="native-action-tiles-grid">
              <button
                type="button"
                className="action-tile"
                onClick={() => setSubView("orders")}
              >
                <span className="tile-icon">📦</span>
                <span className="tile-label">Orders</span>
                {activePendingOrders.length > 0 && (
                  <span className="tile-badge">{activePendingOrders.length}</span>
                )}
              </button>

              <button
                type="button"
                className="action-tile"
                onClick={() => setSubView("products")}
              >
                <span className="tile-icon">🛍️</span>
                <span className="tile-label">Products</span>
              </button>

              <button
                type="button"
                className="action-tile"
                onClick={() => setSubView("settings")}
              >
                <span className="tile-icon">⚙️</span>
                <span className="tile-label">Settings &amp; UPI</span>
              </button>

              <button
                type="button"
                className="action-tile"
                onClick={() => setSubView("customers")}
              >
                <span className="tile-icon">👥</span>
                <span className="tile-label">Customers</span>
              </button>

              <button
                type="button"
                className="action-tile"
                onClick={() => setSubView("share")}
              >
                <span className="tile-icon">📣</span>
                <span className="tile-label">Share &amp; QR</span>
              </button>

              <button
                type="button"
                className="action-tile"
                onClick={onBrowseStores}
              >
                <span className="tile-icon">🚀</span>
                <span className="tile-label">New Store</span>
              </button>
            </div>
          </div>

          {/* Recent Orders Preview Card */}
          <div className="native-recent-orders-card">
            <div className="recent-orders-head">
              <h3>Recent Orders</h3>
              {orders.length > 0 && (
                <button
                  type="button"
                  className="btn-view-all-orders"
                  onClick={() => setSubView("orders")}
                >
                  View All ({orders.length}) ›
                </button>
              )}
            </div>

            {loadingOrders ? (
              <div className="native-loading-box">
                <span className="native-spinner" />
                <span>Loading recent orders…</span>
              </div>
            ) : orders.length === 0 ? (
              <div className="native-no-orders-box">
                <span>🛍️</span>
                <p>No orders yet. Share your store link on WhatsApp to get your first order!</p>
              </div>
            ) : (
              <div className="recent-orders-list">
                {orders.slice(0, 3).map((order) => {
                  const raw = String(order.customer?.phone || "").replace(/\D/g, "");
                  const phone = raw.length === 10 ? `91${raw}` : raw;
                  const waUrl = phone
                    ? `https://wa.me/${phone}?text=${encodeURIComponent(
                        `Hello ${order.customer?.name || "Customer"}, this is ${selectedSite.name}. Regarding your order #${order.id} for ${money(order.total)}.`
                      )}`
                    : null;

                  return (
                    <div key={order.id} className="recent-order-row">
                      <div className="ror-left">
                        <div className="ror-title-line">
                          <b>#{order.id}</b>
                          <span>•</span>
                          <span>{order.customer?.name || "Customer"}</span>
                          <span>•</span>
                          <span className={`ror-status ${order.status?.toLowerCase()}`}>
                            {order.status || "Pending"}
                          </span>
                        </div>
                        <span className="ror-amount">{money(order.total)}</span>
                      </div>

                      <div className="ror-actions">
                        {waUrl && (
                          <a
                            href={waUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ror-wa"
                            title="Chat on WhatsApp"
                          >
                            💬
                          </a>
                        )}
                        {(order.status || "").toLowerCase() === "pending" && (
                          <button
                            type="button"
                            className="btn-ror-accept"
                            onClick={() => handleUpdateOrderStatus(order.id, "Confirmed")}
                            title="Confirm Order"
                          >
                            ✓
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: DEDICATED MOBILE ORDERS VIEW
// =========================================================================
function MobileOrdersView({ orders, loading, onRefresh, onBack, onUpdateStatus, onMarkPaid, storeName }) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const filtered = useMemo(() => {
    let list = orders;
    if (filter !== "all") {
      list = list.filter((o) => (o.status || "").toLowerCase() === filter.toLowerCase());
    }
    const q = query.toLowerCase().trim();
    if (q) {
      list = list.filter(
        (o) =>
          String(o.id).includes(q) ||
          (o.customer?.name && o.customer.name.toLowerCase().includes(q)) ||
          (o.customer?.phone && o.customer.phone.includes(q))
      );
    }
    return list;
  }, [orders, filter, query]);

  const pendingCount = useMemo(() => {
    return orders.filter((o) => (o.status || "").toLowerCase() === "pending").length;
  }, [orders]);

  const updateStatus = async (id, status) => {
    setUpdatingId(id);
    await onUpdateStatus(id, status);
    setUpdatingId(null);
  };

  const markPaid = async (id) => {
    setUpdatingId(id);
    await onMarkPaid(id);
    setUpdatingId(null);
  };

  return (
    <div className="native-subview-wrapper">
      <div className="native-subview-appbar">
        <button type="button" className="btn-subview-back" onClick={onBack}>
          ‹ Back
        </button>
        <span className="subview-header-title">Orders ({orders.length})</span>
        <button type="button" className="btn-subview-refresh" onClick={onRefresh} title="Refresh">
          ↻
        </button>
      </div>

      <div className="native-subview-body">
        {/* Search */}
        <div className="native-search-container" style={{ margin: "0 0 10px" }}>
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="native-search-input"
            placeholder="Search by customer name, phone, order #…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Filter Chips */}
        <div className="native-orders-filter-scroller">
          {["all", "pending", "confirmed", "shipped", "delivered", "cancelled"].map((f) => (
            <button
              key={f}
              type="button"
              className={`order-filter-chip ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === "pending" && pendingCount > 0 && (
                <span className="chip-badge">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="native-loading-box">
            <span className="native-spinner" />
            <span>Loading orders…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="native-empty-card">
            <span style={{ fontSize: "36px" }}>📦</span>
            <h4>No {filter !== "all" ? filter : ""} orders found</h4>
            <p>New orders placed on your storefront will appear here instantly.</p>
          </div>
        ) : (
          <div className="native-orders-card-list">
            {filtered.map((order) => {
              const raw = String(order.customer?.phone || "").replace(/\D/g, "");
              const phone = raw.length === 10 ? `91${raw}` : raw;
              const waUrl = phone
                ? `https://wa.me/${phone}?text=${encodeURIComponent(
                    `Hello ${order.customer?.name || "Customer"}, this is ${storeName || "our store"}. Regarding your order #${order.id} for ${money(order.total)}.`
                  )}`
                : null;
              const isPending = (order.status || "").toLowerCase() === "pending";
              const isConfirmed = (order.status || "").toLowerCase() === "confirmed";
              const isShipped = (order.status || "").toLowerCase() === "shipped";

              return (
                <div key={order.id} className="native-order-item-card">
                  <div className="order-item-head">
                    <div>
                      <span className="order-id-label">#{order.id}</span>
                      <span className="order-date-label">{dateText(order.createdAt)}</span>
                    </div>
                    <span className={`order-status-badge ${order.status?.toLowerCase()}`}>
                      {order.status || "Pending"}
                    </span>
                  </div>

                  <div className="order-customer-box">
                    <div className="customer-info-line">
                      <b>👤 {order.customer?.name || "Customer"}</b>
                      <span>📞 {order.customer?.phone || "No phone"}</span>
                    </div>
                    {order.customer?.address && (
                      <p className="customer-address-line">📍 {order.customer.address}</p>
                    )}
                  </div>

                  <div className="order-items-summary">
                    <span className="order-items-text">
                      {order.items && order.items.length > 0
                        ? order.items.map((i) => `${i.quantity || 1}x ${i.name}`).join(", ")
                        : "Store products"}
                    </span>
                    <div className="order-amount-row">
                      <span className="order-total-price">{money(order.total)}</span>
                      <span className={`payment-pill ${order.paymentStatus === "paid" ? "paid" : "pending"}`}>
                        {order.payment || "UPI"} • {order.paymentStatus === "paid" ? "Paid" : "Awaiting"}
                      </span>
                    </div>
                  </div>

                  <div className="order-touch-actions">
                    {waUrl && (
                      <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn-order-wa">
                        💬 WhatsApp
                      </a>
                    )}
                    {phone && (
                      <a href={`tel:${phone}`} className="btn-order-call">
                        📞 Call
                      </a>
                    )}
                    {isPending && (
                      <button
                        type="button"
                        className="btn-order-confirm"
                        disabled={updatingId === order.id}
                        onClick={() => updateStatus(order.id, "Confirmed")}
                      >
                        {updatingId === order.id ? "…" : "✓ Confirm"}
                      </button>
                    )}
                    {isConfirmed && (
                      <button
                        type="button"
                        className="btn-order-ship"
                        disabled={updatingId === order.id}
                        onClick={() => updateStatus(order.id, "Shipped")}
                      >
                        {updatingId === order.id ? "…" : "🚚 Ship"}
                      </button>
                    )}
                    {isShipped && (
                      <button
                        type="button"
                        className="btn-order-deliver"
                        disabled={updatingId === order.id}
                        onClick={() => updateStatus(order.id, "Delivered")}
                      >
                        {updatingId === order.id ? "…" : "🎉 Deliver"}
                      </button>
                    )}
                    {order.payment === "Manual UPI" && order.paymentStatus === "pending" && (
                      <button
                        type="button"
                        className="btn-order-paid"
                        disabled={updatingId === order.id}
                        onClick={() => markPaid(order.id)}
                      >
                        💰 Mark Paid
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: DEDICATED MOBILE PRODUCTS VIEW
// =========================================================================
function MobileProductsView({ siteId, products, loading, onRefresh, onBack, notify }) {
  const [selectedCat, setSelectedCat] = useState("all");
  const [query, setQuery] = useState("");
  const [editingProduct, setEditingProduct] = useState(null); // null | product | { isNew: true }

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.cat) set.add(p.cat);
    });
    return ["all", ...Array.from(set)];
  }, [products]);

  const filtered = useMemo(() => {
    let list = products;
    if (selectedCat !== "all") {
      list = list.filter((p) => p.cat === selectedCat);
    }
    const q = query.toLowerCase().trim();
    if (q) {
      list = list.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(q)) ||
          (p.cat && p.cat.toLowerCase().includes(q))
      );
    }
    return list;
  }, [products, selectedCat, query]);

  // Toggle Stock Status
  const handleToggleStock = async (product) => {
    const newAvail = product.availability === "Out of stock" ? "In stock" : "Out of stock";
    try {
      await updateProduct(siteId, product.id, { availability: newAvail });
      notify(`${product.name} is now ${newAvail}!`, "success");
      onRefresh();
    } catch {
      notify("Failed to update stock status", "error");
    }
  };

  // Delete Product
  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      await deleteProduct(siteId, product.id);
      notify(`Deleted "${product.name}"`, "info");
      onRefresh();
    } catch {
      notify("Failed to delete product", "error");
    }
  };

  return (
    <div className="native-subview-wrapper">
      <div className="native-subview-appbar">
        <button type="button" className="btn-subview-back" onClick={onBack}>
          ‹ Back
        </button>
        <span className="subview-header-title">Products ({products.length})</span>
        <button
          type="button"
          className="btn-add-product-header"
          onClick={() => setEditingProduct({ isNew: true })}
        >
          + Add
        </button>
      </div>

      <div className="native-subview-body">
        {/* Search */}
        <div className="native-search-container" style={{ margin: "0 0 10px" }}>
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="native-search-input"
            placeholder="Search products by name or category…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Category Pills */}
        {categories.length > 2 && (
          <div className="native-orders-filter-scroller">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                className={`order-filter-chip ${selectedCat === c ? "active" : ""}`}
                onClick={() => setSelectedCat(c)}
              >
                {c === "all" ? "All Categories" : c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="native-loading-box">
            <span className="native-spinner" />
            <span>Loading products…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="native-empty-card">
            <span style={{ fontSize: "36px" }}>🛍️</span>
            <h4>No products found</h4>
            <p>Add your first product to start selling on your storefront.</p>
            <button
              type="button"
              className="btn-native-primary"
              style={{ maxWidth: "220px", margin: "10px auto 0" }}
              onClick={() => setEditingProduct({ isNew: true })}
            >
              + Add Product
            </button>
          </div>
        ) : (
          <div className="native-products-grid">
            {filtered.map((prod) => {
              const inStock = prod.availability !== "Out of stock";
              return (
                <div key={prod.id} className="native-product-card">
                  <div className="prod-img-box">
                    {prod.img ? (
                      <img
                        src={prod.img}
                        alt={prod.name}
                        className="prod-img"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div className="prod-img-fallback" style={{ display: prod.img ? "none" : "flex" }}>
                      🛍️
                    </div>
                    <button
                      type="button"
                      className={`prod-stock-pill ${inStock ? "in-stock" : "out-of-stock"}`}
                      onClick={() => handleToggleStock(prod)}
                      title="Tap to toggle stock"
                    >
                      {inStock ? "✓ In Stock" : "✕ Out of Stock"}
                    </button>
                  </div>

                  <div className="prod-info-box">
                    <span className="prod-cat-tag">{prod.cat || "Store Item"}</span>
                    <h4 className="prod-name">{prod.name}</h4>
                    <div className="prod-price-row">
                      <b className="prod-price">{money(prod.price)}</b>
                      {prod.originalPrice > prod.price && (
                        <del className="prod-mrp">{money(prod.originalPrice)}</del>
                      )}
                    </div>
                  </div>

                  <div className="prod-card-actions">
                    <button
                      type="button"
                      className="btn-prod-edit"
                      onClick={() => setEditingProduct(prod)}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      type="button"
                      className="btn-prod-delete"
                      onClick={() => handleDeleteProduct(prod)}
                      title="Delete Product"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating Add Product Button */}
      <button
        type="button"
        className="native-fab-add"
        onClick={() => setEditingProduct({ isNew: true })}
        aria-label="Add New Product"
      >
        + Add Product
      </button>

      {/* Product Add / Edit Bottom Sheet */}
      {editingProduct && (
        <MobileProductSheet
          siteId={siteId}
          product={editingProduct.isNew ? null : editingProduct}
          onClose={() => setEditingProduct(null)}
          notify={notify}
          onSaved={() => {
            setEditingProduct(null);
            onRefresh();
          }}
        />
      )}
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: MOBILE ADD / EDIT PRODUCT BOTTOM SHEET
// =========================================================================
function MobileProductSheet({ siteId, product, onClose, notify, onSaved }) {
  const isEdit = Boolean(product && product.id);
  const [name, setName] = useState(product?.name || "");
  const [cat, setCat] = useState(product?.cat || "");
  const [price, setPrice] = useState(product?.price || "");
  const [originalPrice, setOriginalPrice] = useState(product?.originalPrice || "");
  const [stock, setStock] = useState(product?.stock ?? 50);
  const [availability, setAvailability] = useState(product?.availability || "In stock");
  const [desc, setDesc] = useState(product?.desc || "");
  const [img, setImg] = useState(product?.img || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const res = await uploadMedia(siteId, file, "product");
      if (res.data?.url) {
        setImg(res.data.url);
        notify("Photo uploaded successfully!", "success");
      }
    } catch {
      notify("Failed to upload image. Choose a smaller file.", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      notify("Please enter product name", "error");
      return;
    }
    const payload = {
      name: name.trim(),
      cat: cat.trim() || "General",
      price: Number(price) || 0,
      originalPrice: Number(originalPrice) || 0,
      stock: Number(stock) || 0,
      availability,
      desc: desc.trim(),
      img: img.trim(),
    };
    try {
      setSaving(true);
      if (isEdit) {
        await updateProduct(siteId, product.id, payload);
        notify(`Updated "${name}"`, "success");
      } else {
        await createProduct(siteId, payload);
        notify(`Added "${name}" to store!`, "success");
      }
      onSaved();
    } catch (err) {
      notify(err.response?.data?.message || "Failed to save product", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="native-bottom-sheet-overlay" onClick={onClose}>
      <div className="native-bottom-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-drag-handle" />

        <div className="sheet-store-header">
          <span style={{ fontSize: "28px" }}>🛍️</span>
          <div className="sheet-store-info">
            <h3 className="sheet-store-title">{isEdit ? "Edit Product" : "Add New Product"}</h3>
            <span className="sheet-store-type">Instant storefront update</span>
          </div>
          <button type="button" className="sheet-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <form className="sheet-form-body" onSubmit={handleSave}>
          <div className="sheet-input-group">
            <label>Product Name</label>
            <input
              type="text"
              placeholder="e.g. Pure Silk Embroidered Kurti"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="sheet-input-group">
            <label>Category</label>
            <input
              type="text"
              placeholder="e.g. Kurtis, Sarees, Cakes, Gifts"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div className="sheet-input-group">
              <label>Selling Price (₹)</label>
              <input
                type="number"
                placeholder="e.g. 799"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>
            <div className="sheet-input-group">
              <label>MRP / Cut Price (₹)</label>
              <input
                type="number"
                placeholder="e.g. 1499"
                value={originalPrice}
                onChange={(e) => setOriginalPrice(e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <div className="sheet-input-group">
              <label>Stock Qty</label>
              <input
                type="number"
                value={stock}
                onChange={(e) => setStock(e.target.value)}
              />
            </div>
            <div className="sheet-input-group">
              <label>Availability</label>
              <select
                value={availability}
                onChange={(e) => setAvailability(e.target.value)}
              >
                <option value="In stock">In Stock</option>
                <option value="Out of stock">Out of Stock</option>
              </select>
            </div>
          </div>

          {/* Product Image */}
          <div className="sheet-input-group">
            <label>Product Image</label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="url"
                placeholder="Paste image URL (https://…)"
                value={img}
                onChange={(e) => setImg(e.target.value)}
                style={{ flex: 1 }}
              />
              <label className="btn-upload-file-label">
                {uploading ? "…" : "📷 Upload"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                  disabled={uploading}
                />
              </label>
            </div>
            {img && (
              <div className="img-preview-chip">
                <img src={img} alt="Preview" />
                <span>Image loaded</span>
              </div>
            )}
          </div>

          <div className="sheet-input-group">
            <label>Description (Optional)</label>
            <textarea
              rows={2}
              placeholder="Key product features, fabric, care instructions…"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="sheet-textarea"
            />
          </div>

          <div className="sheet-sticky-footer">
            <button type="submit" className="btn-sheet-submit" disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save Product Changes" : "⚡ Add Product to Store"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: DEDICATED MOBILE STORE SETTINGS VIEW
// =========================================================================
function MobileStoreSettingsView({ site, onBack, notify, onSettingsUpdated }) {
  const [storeName, setStoreName] = useState(site?.name || "");
  const [contact, setContact] = useState("");
  const [orderWhatsapp, setOrderWhatsapp] = useState("");
  const [upiId, setUpiId] = useState("");
  const [upiName, setUpiName] = useState("");
  const [shipping, setShipping] = useState("0");
  const [freeShippingAbove, setFreeShippingAbove] = useState("0");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!site?.id) return;
    getSiteSettings(site.id)
      .then((res) => {
        if (res.data?.settings) {
          const s = res.data.settings;
          setStoreName(s.storeName || site.name || "");
          setContact(s.contact || "");
          setOrderWhatsapp(s.orderWhatsapp || "");
          setUpiId(s.upiId || "");
          setUpiName(s.upiName || "");
          setShipping(String(s.shipping ?? "0"));
          setFreeShippingAbove(String(s.freeShippingAbove ?? "0"));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [site]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updateSiteSettings(site.id, {
        storeName,
        contact,
        orderWhatsapp,
        upiId,
        upiName,
        shipping,
        freeShippingAbove,
      });
      notify("Store settings saved successfully!", "success");
      onSettingsUpdated();
    } catch {
      notify("Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="native-subview-wrapper">
      <div className="native-subview-appbar">
        <button type="button" className="btn-subview-back" onClick={onBack}>
          ‹ Back
        </button>
        <span className="subview-header-title">Store Settings</span>
      </div>

      <div className="native-subview-body">
        {loading ? (
          <div className="native-loading-box">
            <span className="native-spinner" />
            <span>Loading settings…</span>
          </div>
        ) : (
          <form className="native-settings-form" onSubmit={handleSave}>
            <div className="settings-section-card">
              <h4>🏪 Store Identity</h4>
              <div className="native-field-group">
                <label>Shop / Brand Name</label>
                <input
                  type="text"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  required
                />
              </div>

              <div className="native-field-group">
                <label>Contact Phone Number</label>
                <input
                  type="tel"
                  placeholder="e.g. 9876543210"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                />
              </div>

              <div className="native-field-group">
                <label>Store Subdomain</label>
                <span className="field-readonly-val">https://{site.slug}.ownezzy.com</span>
              </div>
            </div>

            <div className="settings-section-card">
              <h4>📲 Direct UPI (0% Platform Fee)</h4>
              <p className="section-help-text">
                Receive 100% payments directly into your bank account via GPay, PhonePe, or Paytm with zero commission.
              </p>
              <div className="native-field-group">
                <label>Your UPI ID (VPA)</label>
                <input
                  type="text"
                  placeholder="e.g. yourname@okaxis or 9876543210@ybl"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                />
              </div>

              <div className="native-field-group">
                <label>Payee Name (as shown in UPI App)</label>
                <input
                  type="text"
                  placeholder="e.g. Maya Boutique"
                  value={upiName}
                  onChange={(e) => setUpiName(e.target.value)}
                />
              </div>

              <div className="native-field-group">
                <label>WhatsApp Number for Order Alerts</label>
                <input
                  type="tel"
                  placeholder="e.g. 9876543210"
                  value={orderWhatsapp}
                  onChange={(e) => setOrderWhatsapp(e.target.value)}
                />
              </div>
            </div>

            <div className="settings-section-card">
              <h4>🚚 Delivery Charges</h4>
              <div className="native-field-group">
                <label>Standard Delivery Fee (₹)</label>
                <input
                  type="number"
                  placeholder="e.g. 50 (0 for free delivery)"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                />
              </div>

              <div className="native-field-group">
                <label>Free Delivery for Orders Above (₹)</label>
                <input
                  type="number"
                  placeholder="e.g. 499 (0 for no threshold)"
                  value={freeShippingAbove}
                  onChange={(e) => setFreeShippingAbove(e.target.value)}
                />
              </div>
            </div>

            <button type="submit" className="btn-native-primary" disabled={saving}>
              {saving ? "Saving Changes…" : "Save Store Settings"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: DEDICATED MOBILE CUSTOMERS VIEW
// =========================================================================
function MobileCustomersView({ orders, onBack, storeName }) {
  const [query, setQuery] = useState("");

  // Aggregate customers from orders
  const customers = useMemo(() => {
    const map = new Map();
    orders.forEach((o) => {
      const phone = (o.customer?.phone || "").trim();
      const email = (o.customer?.email || "").trim();
      const key = phone || email || `order_${o.id}`;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: o.customer?.name || "Customer",
          phone: o.customer?.phone || "",
          email: o.customer?.email || "",
          address: o.customer?.address || "",
          orderCount: 0,
          totalSpent: 0,
          lastOrderDate: o.createdAt,
        });
      }
      const c = map.get(key);
      c.orderCount += 1;
      c.totalSpent += Number(o.total) || 0;
    });
    return Array.from(map.values());
  }, [orders]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.phone && c.phone.includes(q)) ||
        (c.address && c.address.toLowerCase().includes(q))
    );
  }, [customers, query]);

  return (
    <div className="native-subview-wrapper">
      <div className="native-subview-appbar">
        <button type="button" className="btn-subview-back" onClick={onBack}>
          ‹ Back
        </button>
        <span className="subview-header-title">Customers ({customers.length})</span>
      </div>

      <div className="native-subview-body">
        <div className="native-search-container" style={{ margin: "0 0 12px" }}>
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="native-search-input"
            placeholder="Search by customer name or phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {filtered.length === 0 ? (
          <div className="native-empty-card">
            <span style={{ fontSize: "36px" }}>👥</span>
            <h4>No customers found</h4>
            <p>Customers who place an order on your store will appear here automatically.</p>
          </div>
        ) : (
          <div className="native-customers-list">
            {filtered.map((c) => {
              const raw = String(c.phone || "").replace(/\D/g, "");
              const phone = raw.length === 10 ? `91${raw}` : raw;
              const waUrl = phone
                ? `https://wa.me/${phone}?text=${encodeURIComponent(
                    `Hello ${c.name}, this is ${storeName || "our store"}. Thank you for ordering with us!`
                  )}`
                : null;

              return (
                <div key={c.id} className="native-customer-card">
                  <div className="customer-avatar-box">
                    {c.name.charAt(0).toUpperCase()}
                  </div>

                  <div className="customer-info-box">
                    <h4 className="customer-name">{c.name}</h4>
                    {c.phone && <span className="customer-phone">📞 {c.phone}</span>}
                    {c.address && <p className="customer-addr">📍 {c.address}</p>}

                    <div className="customer-stats-row">
                      <span>Orders: <b>{c.orderCount}</b></span>
                      <span>Total: <b>{money(c.totalSpent)}</b></span>
                    </div>
                  </div>

                  <div className="customer-action-btns">
                    {waUrl && (
                      <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn-cust-wa" title="WhatsApp Customer">
                        💬
                      </a>
                    )}
                    {phone && (
                      <a href={`tel:${phone}`} className="btn-cust-call" title="Call Customer">
                        📞
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: DEDICATED MOBILE SHARE & QR CODE SCREEN
// =========================================================================
function MobileShareQrView({ site, onBack, notify }) {
  const storeUrl = `https://${site.slug}.ownezzy.com`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(storeUrl)}`;

  const copyLink = () => {
    navigator.clipboard?.writeText(storeUrl);
    notify("Store link copied to clipboard!", "success");
  };

  const shareText = `Check out our official online store: ${storeUrl}\n\nShop our complete product collection online with direct UPI payments & fast delivery!`;

  return (
    <div className="native-subview-wrapper">
      <div className="native-subview-appbar">
        <button type="button" className="btn-subview-back" onClick={onBack}>
          ‹ Back
        </button>
        <span className="subview-header-title">Share &amp; Promote</span>
      </div>

      <div className="native-subview-body">
        {/* Link Card */}
        <div className="native-share-link-card">
          <span className="slc-title">Your Official Store Website</span>
          <span className="slc-url-text">{storeUrl}</span>
          <div className="slc-buttons">
            <button type="button" className="btn-slc-copy" onClick={copyLink}>
              📋 Copy Link
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-slc-wa"
            >
              💬 WhatsApp Share
            </a>
          </div>
        </div>

        {/* QR Code Card */}
        <div className="native-qr-card">
          <h3>Storefront QR Code</h3>
          <p>Display in your physical shop or on flyers so customers scan &amp; order online.</p>

          <div className="qr-image-holder">
            <img src={qrUrl} alt={`QR Code for ${site.name}`} className="qr-image" />
          </div>

          <a href={qrUrl} target="_blank" rel="noopener noreferrer" className="btn-download-qr">
            ⬇ View Full QR Code
          </a>
        </div>

        {/* Ready-made Messages */}
        <div className="native-promo-messages-card">
          <h3>Ready-to-Send WhatsApp Messages</h3>
          <div className="promo-message-box">
            <p>
              &quot;🎉 Excited to announce our official online store! Browse our collection and order directly with easy UPI payments: {storeUrl}&quot;
            </p>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(
                `🎉 Excited to announce our official online store! Browse our collection and order directly with easy UPI payments: ${storeUrl}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-send-promo"
            >
              💬 Send Announcement
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: NATIVE STORE PREVIEW & SHOWCASE SHEET
// =========================================================================
function MobileStorePreviewSheet({ store, onClose, onLaunchStore }) {
  const [showIframe, setShowIframe] = useState(false);

  return (
    <div className="native-bottom-sheet-overlay" onClick={onClose}>
      <div className="native-bottom-sheet preview-sheet-custom" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-drag-handle" />

        <div className="sheet-store-header">
          <span className="sheet-store-icon">{store.icon}</span>
          <div className="sheet-store-info">
            <h3 className="sheet-store-title">{store.name}</h3>
            <span className="sheet-store-type">{store.label} • {store.demo}.ownezzy.com</span>
          </div>
          <button type="button" className="sheet-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="sheet-preview-content">
          {/* Snapshot or Iframe Toggle */}
          {showIframe ? (
            <div className="interactive-iframe-container">
              <iframe
                src={store.url || `https://${store.demo}.ownezzy.com`}
                title={`Preview of ${store.name}`}
                className="interactive-iframe"
              />
            </div>
          ) : (
            <div className="native-preview-mockup-frame">
              <img
                src={store.preview}
                alt={store.name}
                className="preview-mockup-img"
              />
              <div className="preview-floating-badges">
                <span className="pf-badge live">● Live Demo Store</span>
                <span className="pf-badge plan">₹399/mo (7-Day Free Trial)</span>
              </div>
            </div>
          )}

          {/* Feature Highlights */}
          <div className="preview-features-grid">
            <div className="pfg-item">⚡ Instant 60s Launch</div>
            <div className="pfg-item">💰 0% Platform Commission</div>
            <div className="pfg-item">📲 WhatsApp Order Alerts</div>
            <div className="pfg-item">🔒 Free Subdomain &amp; SSL</div>
          </div>

          <p className="preview-pitch-text">{store.pitch || store.note}</p>

          <div className="preview-sheet-toggle-row">
            <button
              type="button"
              className="btn-toggle-iframe"
              onClick={() => setShowIframe(!showIframe)}
            >
              {showIframe ? "👁️ Show Template Overview" : "🌐 Try Interactive Website"}
            </button>
            <a
              href={store.url || `https://${store.demo}.ownezzy.com`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-open-browser-tab"
            >
              ↗ Open in Browser
            </a>
          </div>
        </div>

        <div className="sheet-sticky-footer">
          <button
            type="button"
            className="btn-sheet-submit"
            onClick={() => onLaunchStore(store)}
          >
            ⚡ Get This Store (7 Days Free)
          </button>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: 60s STORE LAUNCHER & INSTANT BUY SHEET
// =========================================================================
function NativeStoreLauncherSheet({ store, user, notify, onClose, onStoreCreated, onGoToAdmin }) {
  const dispatch = useDispatch();
  const [step, setStep] = useState(user ? "shopDetails" : "credentials");

  // Form Fields
  const [shopName, setShopName] = useState(store ? `${store.name} Store` : "");
  const [subdomain, setSubdomain] = useState(
    store ? store.demo.toLowerCase().replace(/[^a-z0-9]/g, "") : ""
  );
  const [plan, setPlan] = useState("monthly");

  // Guest Registration Fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");

  const [loading, setLoading] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);

  const handleShopNameChange = (val) => {
    setShopName(val);
    const slug = val.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-");
    setSubdomain(slug);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email || !phone) {
      notify("Please enter your email and WhatsApp number", "error");
      return;
    }
    try {
      setLoading(true);
      const res = await sendOnboardingOtp(phone, email);
      setOtpSent(true);
      notify("Verification code sent to your email! (Test code: " + (res.data?.devOtp || "123456") + ")", "info");
      if (res.data?.devOtp) setOtp(res.data.devOtp);
    } catch (err) {
      notify(err.response?.data?.message || "Failed to send verification code", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) {
      notify("Please enter the 6-digit verification code", "error");
      return;
    }
    try {
      setLoading(true);
      const res = await verifyOnboardingOtp(phone, email, otp);
      setVerificationToken(res.data.verificationToken);
      notify("Email verified successfully!", "success");
      setStep("shopDetails");
    } catch (err) {
      notify(err.response?.data?.message || "Invalid verification code", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStore = async (e) => {
    e.preventDefault();
    if (!shopName.trim()) {
      notify("Please enter a shop name", "error");
      return;
    }
    try {
      setLoading(true);
      const templateKey = store.template || store.baseId || store.id;

      let res;
      if (user) {
        res = await client.post("/onboarding/user-store", {
          shopName,
          subdomain,
          template: templateKey,
          plan,
        });
      } else {
        res = await client.post("/onboarding/stores", {
          verificationToken,
          shopName,
          template: templateKey,
          plan,
          adminPassword: password || undefined,
        });
        if (res.data?.details?.adminId) {
          try {
            const loginRes = await apiLogin({
              login: email,
              password: password || res.data.details.adminPassword,
            });
            dispatch(setUser(loginRes.data.user));
          } catch {
            // continue
          }
        }
      }

      const storeData = res.data?.store || {
        name: shopName,
        slug: subdomain,
        url: `https://${subdomain}.ownezzy.com`,
      };

      setCreatedResult({
        ...storeData,
        details: res.data?.details,
      });
      setStep("success");
      notify("🎉 Your website has been created successfully!", "success");
      onStoreCreated?.(storeData);
    } catch (err) {
      notify(err.response?.data?.message || "Could not create store. Try another name.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="native-bottom-sheet-overlay" onClick={onClose}>
      <div className="native-bottom-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-drag-handle" />

        <div className="sheet-store-header">
          <span className="sheet-store-icon">{store.icon}</span>
          <div className="sheet-store-info">
            <h3 className="sheet-store-title">{store.name}</h3>
            <span className="sheet-store-type">{store.label}</span>
          </div>
          <button type="button" className="sheet-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="sheet-pricing-banner">
          <div className="spb-left">
            <span className="spb-label">Subscription:</span>
            <b className="spb-rate">₹399 / month</b>
          </div>
          <span className="spb-trial-pill">7-Day Free Trial (₹0 today)</span>
        </div>

        {/* STEP 1: Registration / Verification for Guests */}
        {step === "credentials" && (
          <form className="sheet-form-body" onSubmit={otpSent ? handleVerifyOtp : handleSendOtp}>
            <p className="sheet-instruction">
              Enter your WhatsApp number &amp; email to create your store. We will email you a 6-digit code.
            </p>

            <div className="sheet-input-group">
              <label>Full Name</label>
              <input
                type="text"
                placeholder="e.g. Anand Sharma"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={otpSent}
              />
            </div>

            <div className="sheet-input-group">
              <label>WhatsApp Number</label>
              <input
                type="tel"
                placeholder="e.g. 9876543210"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                disabled={otpSent}
              />
            </div>

            <div className="sheet-input-group">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={otpSent}
              />
            </div>

            {otpSent && (
              <>
                <div className="sheet-input-group">
                  <label>6-Digit Verification Code</label>
                  <input
                    type="text"
                    placeholder="Enter code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.trim())}
                    maxLength={6}
                    required
                  />
                </div>
                <div className="sheet-input-group">
                  <label>Password (Optional)</label>
                  <input
                    type="password"
                    placeholder="Min 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="sheet-sticky-footer">
              <button type="submit" className="btn-sheet-submit" disabled={loading}>
                {loading ? "Please wait…" : otpSent ? "Verify Code & Proceed" : "Send Verification Code"}
              </button>
            </div>
          </form>
        )}

        {/* STEP 2: Shop Details & Launch */}
        {step === "shopDetails" && (
          <form className="sheet-form-body" onSubmit={handleCreateStore}>
            {user && (
              <div className="sheet-user-pill">
                <span>✓</span>
                <span>
                  Logged in as <b>{user.name}</b> ({user.email}). Zero OTP needed!
                </span>
              </div>
            )}

            <div className="sheet-input-group">
              <label>Your Shop / Brand Name</label>
              <input
                type="text"
                placeholder="e.g. Maya Fashion Boutique"
                value={shopName}
                onChange={(e) => handleShopNameChange(e.target.value)}
                required
              />
            </div>

            <div className="sheet-input-group">
              <label>Website Subdomain</label>
              <input
                type="text"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                required
              />
              <span className="sheet-subdomain-hint">
                🌐 Live Link: https://{subdomain || "yourshop"}.ownezzy.com
              </span>
            </div>

            <div className="sheet-input-group">
              <label>Choose Plan</label>
              <select value={plan} onChange={(e) => setPlan(e.target.value)}>
                <option value="monthly">Monthly Plan — ₹399/mo (7-Day Free Trial, then ₹399/mo)</option>
                <option value="yearly">Yearly Plan — ₹4,499/yr (Save 6% — 7-Day Free Trial)</option>
                <option value="trial">7-Day Free Trial (₹0 today — No credit card required)</option>
              </select>
            </div>

            <div className="sheet-features-checklist">
              <div className="sfc-item">✓ Complete store ready with demo products</div>
              <div className="sfc-item">✓ Direct UPI payments with 0% commission</div>
              <div className="sfc-item">✓ Automated WhatsApp order confirmations</div>
              <div className="sfc-item">✓ Free SSL certificate &amp; custom domain support</div>
            </div>

            <div className="sheet-sticky-footer">
              <button type="submit" className="btn-sheet-submit" disabled={loading}>
                {loading ? "Creating Your Website…" : "⚡ Launch My Store Now"}
              </button>
            </div>
          </form>
        )}

        {/* STEP 3: Success & Download APK */}
        {step === "success" && (
          <div className="sheet-success-body">
            <span className="success-emoji">🎉</span>
            <h3>Your Store is Live!</h3>
            <p>Your online store has been generated and is live on the internet.</p>

            <div className="success-link-card">
              <span className="slc-label">Your Live Storefront:</span>
              <a
                href={createdResult?.url || `https://${subdomain}.ownezzy.com`}
                target="_blank"
                rel="noopener noreferrer"
                className="slc-url"
              >
                {createdResult?.url || `https://${subdomain}.ownezzy.com`}
              </a>
            </div>

            <div className="success-buttons-group">
              <button type="button" className="btn-success-admin" onClick={onGoToAdmin}>
                <span>📊 Open in My Admin Panel</span>
              </button>
              <a
                href="/downloads/ownezzy.apk?v=2.0"
                download="ownezzy.apk"
                className="btn-success-apk"
              >
                <span>📱 Download Ownezzy Mobile APK (v2.0)</span>
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  `Check out our new online store: https://${subdomain}.ownezzy.com\n\nDownload our mobile app: https://admin.ownezzy.com/downloads/ownezzy.apk?v=2.0`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-success-share"
              >
                <span>💬 Share on WhatsApp</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// SUBCOMPONENT: NATIVE AUTH BOTTOM SHEET (LOGIN & REGISTER)
// =========================================================================
function NativeAuthSheet({ mode = "login", onClose, notify, onSuccess }) {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(mode);
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [choice, setChoice] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await apiLogin({ login: loginEmail, password: loginPassword });
      if (res.data?.chooseAccount && res.data.chooseAccount.length > 0) {
        setChoice({ accounts: res.data.chooseAccount, token: res.data.choiceToken });
        return;
      }
      if (res.data?.user) {
        dispatch(setUser(res.data.user));
        onSuccess?.(res.data.user);
      }
    } catch (err) {
      notify(err.response?.data?.message || "Invalid email or password", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePickAccount = async (account) => {
    try {
      setLoading(true);
      const res = await chooseAccount(choice.token, account.id);
      if (res.data?.user) {
        dispatch(setUser(res.data.user));
        onSuccess?.(res.data.user);
      }
    } catch (err) {
      notify(err.response?.data?.message || "Failed to select account", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="native-bottom-sheet-overlay" onClick={onClose}>
      <div className="native-bottom-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-drag-handle" />

        <div className="sheet-auth-header">
          <div className="sheet-auth-tabs">
            <button
              type="button"
              className={`sheet-auth-tab ${activeTab === "login" ? "active" : ""}`}
              onClick={() => setActiveTab("login")}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`sheet-auth-tab ${activeTab === "register" ? "active" : ""}`}
              onClick={() => setActiveTab("register")}
            >
              Register
            </button>
          </div>
          <button type="button" className="sheet-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {choice ? (
          <div className="sheet-form-body account-choice-panel">
            <h3 style={{ margin: "0 0 8px" }}>Select Account</h3>
            <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 16px" }}>
              Your login matches multiple accounts. Tap the account you want to sign in with:
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {choice.accounts.map((acc) => (
                <button
                  key={acc.id}
                  type="button"
                  className="account-choice-tile"
                  onClick={() => handlePickAccount(acc)}
                  disabled={loading}
                >
                  <span style={{ fontSize: "24px" }}>{acc.role === "superadmin" ? "👑" : "🏪"}</span>
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <strong style={{ display: "block", color: "#0f172a", fontSize: "14px" }}>{acc.label || acc.role}</strong>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>{acc.name}</span>
                  </div>
                  <span style={{ color: "#10b981", fontWeight: 700 }}>→</span>
                </button>
              ))}
            </div>
          </div>
        ) : activeTab === "login" ? (
          <form className="sheet-form-body" onSubmit={handleLogin}>
            <div className="sheet-input-group">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
              />
            </div>
            <div className="sheet-input-group">
              <label>Password</label>
              <input
                type="password"
                placeholder="Enter your password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
              />
            </div>

            <div className="sheet-sticky-footer">
              <button type="submit" className="btn-sheet-submit" disabled={loading}>
                {loading ? "Signing In…" : "Sign In to Admin Panel"}
              </button>
            </div>
          </form>
        ) : (
          <div className="sheet-form-body" style={{ textAlign: "center", padding: "24px 16px" }}>
            <span style={{ fontSize: "40px" }}>🏪</span>
            <h3 style={{ margin: "10px 0 6px" }}>Get a Demo Store to Register</h3>
            <p style={{ fontSize: "13.5px", color: "#64748b", margin: "0 0 16px" }}>
              To create your account, choose any of our 100+ business templates and tap &quot;Get This Store&quot;. Your account is created in 60s!
            </p>
            <div className="sheet-sticky-footer">
              <button type="button" className="btn-sheet-submit" onClick={onClose}>
                Browse 100+ Stores
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
